/**
 * Connector List Load Policy
 *
 * Fetches and caches the systems-integrations connector catalog on
 * initialization, then maps each entry to a capability-aware `ConnectorEntry`
 * (carrying `connectorKind` + `capabilities`). Applies connector image
 * overrides from local assets.
 *
 * Renamed from `connectorsPolicy`. It reads the systems-integrations
 * connectors readmodel (still exposed via the `SalesChannelsConnectors`
 * client-library endpoint until the backend endpoint rename lands).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ConnectorKind, Capability } from '@bett3r-dev/teselly-domain';
import { SystemsIntegrationsState, ConnectorEntry } from '../state';
import { SystemsIntegrationsCommand } from '../commands';

// Connector images - imported from assets
import amImage from '@assets/channelImages/amazon.png';
import fenicioImage from '@assets/channelImages/fenicio.png';
import mlImage from '@assets/channelImages/ml.png';
import retailImage from '@assets/channelImages/retail.png';
import shopifyImage from '@assets/channelImages/shopify.png';
import tnImage from '@assets/channelImages/tn.png';
import wooImage from '@assets/channelImages/woo.png';

const imagesMap: Record<string, string> = {
  mercadolibre: mlImage,
  tiendanube: tnImage,
  amazon: amImage,
  shopify: shopifyImage,
  fenicio: fenicioImage,
  woocommerce: wooImage,
  retailstore: retailImage
};

/**
 * Normalizes a raw connector readmodel row into a capability-aware
 * `ConnectorEntry`. Defensive against rows that predate the `connectorKind`
 * / `capabilities` projection fields.
 */
const toConnectorEntry = ( raw: any ): ConnectorEntry => {
  const rawKind = ( raw.type ?? raw.connectorKind ) as string;
  const connectorKind = ( rawKind === 'erp' ? 'ERP' : rawKind ) as ConnectorKind;
  const capabilities = Array.isArray( raw.capabilities )
    ? ( raw.capabilities as Capability[])
    : [];

  return {
    ...raw,
    connectorKind,
    capabilities,
    imageUrl: imagesMap[raw.id] || raw.imageUrl
  } as ConnectorEntry;
};

/**
 * Creates a policy that loads the connector catalog on mount.
 */
export const createConnectorListLoadPolicy = (
  backend: ClientLibraryType
): Policy<SystemsIntegrationsState, SystemsIntegrationsCommand> => ({
  name: 'systems-integrations-connector-list-load',

  init: ({ dispatch }) => {
    dispatch(
      { type: 'integrations/connectors/setLoading', payload: { isLoading: true }},
      { source: 'api' }
    );

    backend.queries.SystemsIntegrationsConnectors({})
      .then(( connectors ) => {
        const connectorsMap = connectors.reduce<Record<string, ConnectorEntry>>(
          ( acc, connector ) => {
            acc[connector.id] = toConnectorEntry( connector );
            return acc;
          },
          {}
        );

        dispatch(
          { type: 'integrations/connectors/set', payload: { connectors: connectorsMap }},
          { source: 'api' }
        );

        dispatch(
          { type: 'integrations/connectors/setLoading', payload: { isLoading: false }},
          { source: 'api' }
        );
      })
      .catch(() => {
        dispatch(
          { type: 'integrations/connectors/setLoading', payload: { isLoading: false }},
          { source: 'api' }
        );
      });
  }
});
